/******************************************** IMPORTS *********************************************/
import {i18nConfig, windowNamespaces} from '../../../config/app/config-client';
import {deepFreeze} from 'mad-utils/lib/shared';

import Polyglot from 'node-polyglot';
import {isDevelopment} from 'env-var-helpers';

// Quick ref to a namespace for convenience access to data and libraries when in development mode
const devNS = windowNamespaces.dev;

/**
 * Store polyglot here after removing from window
 */
let polyglotMemoized: Polyglot;

/**
 * Symbol-based hidden global location for polyglot
 */
export const polyglotSymbol = Symbol('__GLOBAL_POLYGLOT_INSTANCE__');

/********************************************* EXPORT *********************************************/
/**
 * Access the globally stored instance of Polyglot
 *
 * On initial call, get, clone, freeze, and locally memoize the polyglot instance from window
 *  * When all this is complete, delete it from window: all future calls will instead return the
 * memoized version (this improves performance & prevents runtime global namespace pollution)
 *
 * If development mode is active, attach the instance to a 'debugging' global namespace
 *
 * @return {Polyglot} instance of the Polyglot class, hydrated w/ the currently selected language's
 *                    i18n info (translations, links to images with text in said language, etc.)
 */
export const getPolyglot = (): Readonly<Polyglot> => {
    if (polyglotMemoized) return polyglotMemoized;

    polyglotMemoized = Object.freeze(window[i18nConfig.i18nNamespace]);

    if (isDevelopment) {
        if (typeof window[devNS] !== 'undefined' && typeof window[devNS] !== null) {
            window[devNS].polyglot = polyglotMemoized;
        } else {
            window[devNS] = {polyglot: polyglotMemoized};
        }
    }

    Reflect.deleteProperty(window, i18nConfig.i18nNamespace);

    window[polyglotSymbol] = deepFreeze(polyglotMemoized);

    return polyglotMemoized;
};
